import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgress, Typography } from '@material-ui/core';

import { AppContext } from '../../../Context/AppContextProvide';
import { HubSpokeContext } from '../../../Context/HubSpokeContextProvider';
import {
  getScanDashboard,
  getScanDeadInventory,
  getLocations,
} from '../../../Actions';
import { TOUR_PENDING_KEY, resumeScanDashboardStage } from '../../Shared/WhatsNew/tours';

import KpiRow from './KpiRow';
import ScanTrend from './ScanTrend';
import ActivityHeatmap from './ActivityHeatmap';
import EngagementFunnel from './EngagementFunnel';
import ManufacturerShare from './ManufacturerShare';
import Momentum from './Momentum';
import LocationComparison from './LocationComparison';
import DeadInventory from './DeadInventory';

import '../../styles/Insights.css';

// Format a Date as YYYY-MM-DD (local) for the native date inputs / API.
const ymd = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

const SEGMENTS = [
  { key: 'customer', label: 'Customers' },
  { key: 'staff', label: 'Staff' },
  { key: 'all', label: 'All' },
];

const ScanDashboard = () => {
  const history = useHistory();
  const { user } = useContext(AppContext);
  const { spokeList, selectedSpoke, setSelectedSpoke } =
    useContext(HubSpokeContext);
  const isAdmin = user.access_type_cd === 'a';

  // Resume the "What's New" tour if the user arrived here from its 4th stage.
  useEffect(() => {
    if (sessionStorage.getItem(TOUR_PENDING_KEY) === 'scandashboard') {
      sessionStorage.removeItem(TOUR_PENDING_KEY);
      setTimeout(() => resumeScanDashboardStage(history), 500);
    }
  }, [history]);

  const [start, setStart] = useState(ymd(daysAgo(29)));
  const [end, setEnd] = useState(ymd(new Date()));
  const [segment, setSegment] = useState('customer');
  const [printedOnly, setPrintedOnly] = useState(true);
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState([]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Dead inventory loads on its own so its heavier query never blocks the rest.
  const [deadInventory, setDeadInventory] = useState(null);
  const [deadLoading, setDeadLoading] = useState(true);
  const abortRef = useRef(null);

  // Admins get a location filter; load the list once.
  useEffect(() => {
    if (!isAdmin) return;
    getLocations()
      .then((res) => setLocations(res.data?.data || res.data || []))
      .catch(() => setLocations([]));
  }, [isAdmin]);

  const fetchData = useCallback(() => {
    // Cancel any request still in flight so rapid filter changes don't stack
    // and a stale response can't overwrite a newer one.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const isCanceled = (err) =>
      err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError';

    setLoading(true);
    setError('');
    getScanDashboard(
      start,
      end,
      segment,
      locationId || null,
      printedOnly,
      selectedSpoke,
      controller.signal
    )
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        if (isCanceled(err)) return; // superseded by a newer request
        setError(
          err?.response?.data?.message || 'Could not load scan dashboard.'
        );
        setLoading(false);
      });

    // Fire the dead-inventory request in parallel; its panel fills in when ready.
    setDeadLoading(true);
    getScanDeadInventory(
      start,
      end,
      segment,
      locationId || null,
      printedOnly,
      selectedSpoke,
      controller.signal
    )
      .then((res) => {
        setDeadInventory(res.data?.deadInventory || null);
        setDeadLoading(false);
      })
      .catch((err) => {
        if (isCanceled(err)) return;
        setDeadInventory(null);
        setDeadLoading(false);
      });
  }, [start, end, segment, printedOnly, locationId, selectedSpoke]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applyPreset = (n) => {
    setStart(ymd(daysAgo(n - 1)));
    setEnd(ymd(new Date()));
  };

  return (
    <div className="insights__container">
      <div className="insights__header" id="tour-scan-dashboard">
        <Typography variant="h4" className="insights__title">
          Scan Dashboard
        </Typography>
      </div>

      {/* Global filter bar — drives every panel below */}
      <div className="insights__filterbar">
        <div className="insights__filter-field">
          <span className="insights__filter-label">From</span>
          <input
            type="date"
            className="insights__date-input"
            value={start}
            max={end}
            onChange={(e) => setStart(e.target.value)}
          />
        </div>
        <div className="insights__filter-field">
          <span className="insights__filter-label">To</span>
          <input
            type="date"
            className="insights__date-input"
            value={end}
            min={start}
            max={ymd(new Date())}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>
        <div className="insights__filter-field">
          <span className="insights__filter-label">Quick range</span>
          <select
            className="insights__native-select"
            value=""
            onChange={(e) => e.target.value && applyPreset(Number(e.target.value))}
          >
            <option value="">Choose…</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="180">Last 180 days</option>
          </select>
        </div>

        <div className="insights__filter-field">
          <span className="insights__filter-label">Audience</span>
          <span className="insights__segment">
            {SEGMENTS.map((s) => (
              <button
                key={s.key}
                className={segment === s.key ? 'is-active' : ''}
                onClick={() => setSegment(s.key)}
              >
                {s.label}
              </button>
            ))}
          </span>
        </div>

        <div className="insights__filter-field">
          <span className="insights__filter-label">Products</span>
          <span className="insights__segment">
            <button
              className={printedOnly ? 'is-active' : ''}
              onClick={() => setPrintedOnly(true)}
            >
              Printed only
            </button>
            <button
              className={!printedOnly ? 'is-active' : ''}
              onClick={() => setPrintedOnly(false)}
            >
              All
            </button>
          </span>
        </div>

        {isAdmin && locations.length > 0 && (
          <div className="insights__filter-field">
            <span className="insights__filter-label">Location</span>
            <select
              className="insights__native-select"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l.location_id} value={l.location_id}>
                  {l.location_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {isAdmin && spokeList && spokeList.length > 0 && (
          <div className="insights__filter-field">
            <span className="insights__filter-label">Remote site</span>
            <select
              className="insights__native-select"
              value={selectedSpoke}
              onChange={(e) => setSelectedSpoke(e.target.value)}
            >
              <option value="">This site</option>
              {spokeList.map((site) => (
                <option key={site} value={site}>
                  {site}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="insights__filter-spacer" />
      </div>

      {loading && (
        <div className="insights__loading">
          <CircularProgress />
          <div style={{ marginTop: 12 }}>Crunching scan data…</div>
        </div>
      )}

      {!loading && error && <div className="insights__empty">{error}</div>}

      {!loading && !error && data && (
        <>
          <KpiRow kpis={data.kpis} />

          <div className="insights__grid">
            <ScanTrend trend={data.trend} trendPrior={data.trendPrior} />
            <ActivityHeatmap heatmap={data.heatmap} />
          </div>

          <div className="insights__grid">
            <EngagementFunnel funnel={data.funnel} />
          </div>

          <div className="insights__grid">
            <ManufacturerShare manufacturers={data.manufacturers} />
            <Momentum momentum={data.momentum} />
          </div>

          <div className="insights__grid">
            <LocationComparison locations={data.locations} />
            <DeadInventory deadInventory={deadInventory} loading={deadLoading} />
          </div>
        </>
      )}
    </div>
  );
};

export default ScanDashboard;
